import { useState, type ReactNode } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// ─── Label helpers ────────────────────────────────────────────────────────

/** Nicer labels for the well-known CFO v2 scoring keys; everything else
 *  falls back to a snake_case → Title Case conversion. Purely cosmetic —
 *  the engine reads the raw keys. */
const LABEL_MAP: Record<string, string> = {
  stage_points: "Stage Points",
  level_points: "Level Points",
  quartile_points: "Quartile Points",
  paliers: "Paliers",
  recruiting_points: "Recruiting",
  in_place_no_cfo_points: "In place + no finance lead",
  // Common stage / quartile keys.
  love_money: "Love Money",
  pre_seed: "Pre-seed",
  seed: "Seed",
  series_a: "Series A",
  series_b: "Series B",
  series_c: "Series C",
  series_d: "Series D",
  cfo_in_place: "CFO in place",
  none: "None",
  junior: "Junior",
  senior: "Senior",
};

export function humanizeScoringKey(key: string): string {
  if (LABEL_MAP[key]) return LABEL_MAP[key];
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── CollapsibleSection ─────────────────────────────────────────────────────

interface CollapsibleSectionProps {
  title: string;
  description?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

/** Lightweight disclosure — there's no shared Collapsible primitive in
 *  the design system, so this is the local one. The toggle is never
 *  disabled (read-only users can still expand to inspect the config);
 *  the inputs inside carry their own `disabled` state. */
export function CollapsibleSection({
  title,
  description,
  defaultOpen = false,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-slate-900/[0.06] bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="min-w-0">
          <span className="text-sm font-medium text-slate-900">{title}</span>
          {description && (
            <p className="text-xs text-slate-500">{description}</p>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-slate-400 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div className="border-t border-slate-900/[0.06] px-4 py-4">{children}</div>
      )}
    </div>
  );
}

// ─── NumberFieldEditor ──────────────────────────────────────────────────────

interface NumberFieldEditorProps {
  label: string;
  value: number;
  onChange: (val: number) => void;
  suffix?: string;
  min?: number;
  max?: number;
  disabled?: boolean;
}

/** Single labelled integer input (booster windows, stage durations,
 *  scalar dimension points). Empty / non-numeric input resolves to 0. */
export function NumberFieldEditor({
  label,
  value,
  onChange,
  suffix,
  min = 0,
  max,
  disabled,
}: NumberFieldEditorProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="min-w-0 flex-1 text-sm text-slate-700">{label}</span>
      <div className="flex shrink-0 items-center gap-2">
        <Input
          type="number"
          value={value}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(e) => {
            const n = Math.round(Number(e.target.value));
            // Non-numeric input (e.g. a stray letter) is a no-op — leave
            // the previous value rather than forcing 0, matching
            // PointsMapEditor.
            if (!Number.isFinite(n)) return;
            const clampedLow = Math.max(min, n);
            onChange(max != null ? Math.min(max, clampedLow) : clampedLow);
          }}
          className="h-8 w-20 rounded-lg border-slate-200 text-right font-mono text-xs"
        />
        {suffix && <span className="text-xs text-slate-400">{suffix}</span>}
      </div>
    </div>
  );
}

// ─── PointsMapEditor ────────────────────────────────────────────────────────

interface PointsMapEditorProps {
  label: string;
  /** key → points. Rendered in insertion order (stages / levels /
   *  quartiles carry a meaningful order that point-sorting would break). */
  entries: Record<string, number>;
  onChange: (entries: Record<string, number>) => void;
  /** Per-entry cap — usually the dimension's max_points. */
  maxPoints?: number;
  disabled?: boolean;
}

export function PointsMapEditor({
  label,
  entries,
  onChange,
  maxPoints,
  disabled,
}: PointsMapEditorProps) {
  const rows = Object.entries(entries);
  return (
    <div>
      <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
        {rows.map(([key, val]) => (
          <div key={key} className="flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-xs text-slate-600">
              {humanizeScoringKey(key)}
            </span>
            <div className="flex shrink-0 items-center gap-1">
              <Input
                type="number"
                value={val}
                min={0}
                max={maxPoints}
                disabled={disabled}
                onChange={(e) => {
                  const n = Math.round(Number(e.target.value));
                  if (!Number.isFinite(n)) return;
                  const clamped = Math.max(
                    0,
                    maxPoints != null ? Math.min(maxPoints, n) : n,
                  );
                  onChange({ ...entries, [key]: clamped });
                }}
                className="h-8 w-16 rounded-lg border-slate-200 text-right font-mono text-xs"
              />
              <span className="text-[10px] text-slate-400">pts</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── TagListEditor ──────────────────────────────────────────────────────────

interface TagListEditorProps {
  label: string;
  description?: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

/** Removable keyword chips + an inline add input. Case is preserved (so
 *  "Station F" / "Y Combinator" keep their display form) but dedup is
 *  case-insensitive to avoid "CFO" and "cfo" both landing in the list. */
export function TagListEditor({
  label,
  description,
  tags,
  onChange,
  disabled,
  placeholder = "Add keyword…",
}: TagListEditorProps) {
  const [input, setInput] = useState("");

  // Coerce defensively — the wire shape is loose, so an element could
  // arrive as a non-string and crash `.toLowerCase()` / break dedup.
  const safeTags = tags.map((t) => String(t));

  function addTag() {
    if (disabled) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    const exists = safeTags.some(
      (t) => t.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      setInput("");
      return;
    }
    onChange([...safeTags, trimmed]);
    setInput("");
  }

  function removeAt(idx: number) {
    if (disabled) return;
    onChange(safeTags.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-800">{label}</label>
      {description && (
        <p className="mt-0.5 text-xs text-slate-500">{description}</p>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {safeTags.length === 0 && (
          <span className="text-xs text-slate-400">No keywords yet.</span>
        )}
        {safeTags.map((tag, idx) => (
          <span
            key={`${tag}-${idx}`}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeAt(idx)}
                aria-label={`Remove ${tag}`}
                className="text-slate-400 transition-colors hover:text-rose-500"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder={placeholder}
            className="h-8 flex-1 rounded-lg border-slate-200 text-sm"
          />
          <button
            type="button"
            onClick={addTag}
            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-800"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        </div>
      )}
    </div>
  );
}

// ─── DimensionConfigEditor ──────────────────────────────────────────────────

interface DimensionConfigEditorProps {
  /** scoring_config[dimensionKey] — e.g. `{ stage_points: {...} }` or
   *  `{ recruiting_points: 10, in_place_no_cfo_points: 10 }`. */
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Optional legacy UI cap. Registered CFO validates the full profile server-side. */
  maxPoints?: number;
  disabled?: boolean;
}

/** Renders a dimension's breakdown introspectively: a points-map value
 *  (object of numbers) becomes a PointsMapEditor; a scalar number
 *  becomes a NumberFieldEditor. This keeps the editor working for every
 *  CFO v2 dimension (stage_points / level_points / paliers /
 *  quartile_points / recruiting_points / …) without a per-dimension
 *  switch, and tolerates new sub-fields the backend may add. */
export function DimensionConfigEditor({
  config,
  onChange,
  maxPoints,
  disabled,
}: DimensionConfigEditorProps) {
  const entries = Object.entries(config);
  const mapEntries = entries.filter(([, v]) => isNumberMap(v));
  const scalarEntries = entries.filter(([, v]) => typeof v === "number");

  if (mapEntries.length === 0 && scalarEntries.length === 0) {
    return (
      <p className="mt-3 text-xs text-slate-400">
        No editable breakdown for this dimension.
      </p>
    );
  }

  return (
    <div className="mt-3 space-y-4 rounded-xl border border-slate-900/[0.06] bg-slate-50/40 p-4">
      {mapEntries.map(([field, value]) => (
        <PointsMapEditor
          key={field}
          label={humanizeScoringKey(field)}
          entries={value as Record<string, number>}
          maxPoints={maxPoints}
          disabled={disabled}
          onChange={(next) => onChange({ ...config, [field]: next })}
        />
      ))}
      {scalarEntries.length > 0 && (
        <div className="space-y-1">
          {scalarEntries.map(([field, value]) => (
            <NumberFieldEditor
              key={field}
              label={humanizeScoringKey(field)}
              value={value as number}
              max={maxPoints}
              suffix="pts"
              disabled={disabled}
              onChange={(n) => onChange({ ...config, [field]: n })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** True for a non-empty plain object whose values are all finite
 *  numbers — i.e. a `{key: points}` map the PointsMapEditor can render. */
function isNumberMap(v: unknown): v is Record<string, number> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  const vals = Object.values(v);
  return (
    vals.length > 0 &&
    vals.every((x) => typeof x === "number" && Number.isFinite(x))
  );
}
